import { ElementType } from 'react';
import { PolymorphicProps } from '../types';
import { cva, VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

export const TypographyVariant = {
  display010: 'text-32 leading-32 off-font-liga-clig font-normal tracking-zero',
  display020: 'text-48 leading-48 off-font-liga-clig font-normal tracking-zero',
  display030: 'text-104 leading-104 off-font-liga-clig font-normal tracking-zero',
  display040: 'text-120 leading-120 off-font-liga-clig font-normal tracking-zero',
  display050: 'text-136 leading-136 off-font-liga-clig font-normal tracking-zero',
  // display060: '',
  // display070: '',
  // display080: '',

  headline010: 'text-14 leading-18 off-font-liga-clig font-normal',
  headline020: 'text-16 leading-20 off-font-liga-clig font-normal',
  headline025: 'text-20 leading-24 off-font-liga-clig font-normal',
  headline030: 'text-24 leading-28 off-font-liga-clig font-normal',
  headline040: 'text-28 leading-32 off-font-liga-clig font-normal',
  headline050: 'text-32 leading-36 off-font-liga-clig font-normal',
  headline060: 'text-36 leading-40 off-font-liga-clig font-normal',
  headline070: 'text-40 leading-44 off-font-liga-clig font-normal',
  headline080: 'text-44 leading-48 off-font-liga-clig font-normal',

  subheadline010: 'text-12 leading-16 line-height-trim text-edge font-bold tracking-zero',
  subheadline020: 'text-14 leading-18 line-height-trim text-edge font-bold tracking-zero',
  subheadline030: 'text-16 leading-20 line-height-trim text-edge font-bold tracking-zero',
  subheadline040: 'text-18 leading-24 line-height-trim text-edge font-bold tracking-zero',
  subheadline050: 'text-20 leading-24 line-height-trim text-edge font-bold tracking-zero',
  subheadline060: 'text-24 leading-32 line-height-trim text-edge font-bold tracking-zero',
  subheadline070: 'text-28 leading-36 line-height-trim text-edge font-bold tracking-zero',
  subheadline080: 'text-32 leading-36 line-height-trim text-edge font-bold tracking-zero',

  paragraph000: 'text-10 leading-10 off-font-liga-clig font-normal tracking-zero',
  paragraph010: 'text-12 leading-16 off-font-liga-clig font-normal tracking-zero',
  paragraph020: 'text-14 leading-20 off-font-liga-clig font-normal tracking-zero',
  paragraph030: 'text-16 leading-22 off-font-liga-clig font-normal tracking-zero',
  paragraph040: 'text-18 leading-24 off-font-liga-clig font-normal tracking-zero',
  paragraph050: 'text-20 leading-26 off-font-liga-clig font-normal tracking-zero',
  paragraph060: 'text-24 leading-28 off-font-liga-clig font-normal tracking-zero',
  paragraph070: 'text-28 leading-32 off-font-liga-clig font-normal tracking-zero',
  paragraph080: 'text-32 leading-36 off-font-liga-clig font-normal tracking-zero',

  quote010: 'text-24 leading-36 off-font-liga-clig font-normal tracking-zero',
  quote020: 'text-32 leading-48 off-font-liga-clig font-normal tracking-zero',

  caption010: 'text-12 leading-16 off-font-liga-clig font-medium tracking-zero',
  caption020: 'text-14 leading-20 off-font-liga-clig font-medium tracking-zero',
  caption030: 'text-16 leading-22 off-font-liga-clig font-medium tracking-zero',
  caption040: 'text-18 leading-24 off-font-liga-clig font-medium tracking-zero',
  caption050: 'text-20 leading-26 off-font-liga-clig font-medium tracking-zero',
  caption060: 'text-24 leading-28 off-font-liga-clig font-medium tracking-zero',
  caption070: 'text-28 leading-32 off-font-liga-clig font-medium tracking-zero',
  caption080: 'text-32 leading-36 off-font-liga-clig font-medium tracking-zero',

  label000: 'text-8 leading-12 off-font-liga-clig font-normal tracking-zero',
  label010: 'text-12 leading-16 off-font-liga-clig font-normal tracking-zero',
  label020: 'text-14 leading-20 off-font-liga-clig font-normal tracking-zero',
  label030: 'text-16 leading-22 off-font-liga-clig font-normal tracking-zero',
  label040: 'text-18 leading-24 off-font-liga-clig font-normal tracking-zero',
  label050: 'text-20 leading-28 off-font-liga-clig font-normal tracking-zero',
  label060: 'text-24 leading-32 off-font-liga-clig font-normal tracking-zero',

  button010: 'text-12 leading-18 off-font-liga-clig font-bold tracking-zero',
  button020: 'text-14 leading-20 off-font-liga-clig font-bold tracking-zero',
  button030: 'text-16 leading-22 off-font-liga-clig font-bold tracking-zero',
  button040: 'text-18 leading-24 off-font-liga-clig font-bold tracking-zero',

  smallprint010R: 'text-10 leading-14 off-font-liga-clig font-normal tracking-zero',
};

export type TypographyVariants = keyof typeof TypographyVariant;

const TypographyCVA = cva(['font-primary'], {
  variants: {
    variant: TypographyVariant,
  },
  defaultVariants: {
    variant: 'paragraph010',
  },
});

export type TypographyProps<T extends ElementType> = PolymorphicProps<T> &
  VariantProps<typeof TypographyCVA>;

export const Typography = <T extends ElementType = 'p'>({
  as,
  children,
  className,
  variant,
  ...otherProps
}: TypographyProps<T>) => {
  const Component = as || 'p';

  return (
    <Component className={twMerge(clsx(TypographyCVA({ variant }), className))} {...otherProps}>
      {children}
    </Component>
  );
};
